import StatusCommentFullViewDialog from 'components/v2/dialogs/StatusCommentFullViewDialog';
import WizardTextInputComponent from 'components/v2/epd-wizard-inputs/WizardTextInput';
import { FileRow } from 'components/v2/file-row/FileRow';
import { HelpBox } from 'components/v2/help-boxes';
import { STATUS_COMMENT_MAX_LENGTH } from 'constants/constants';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionState, EPDCopyGroupStatus, EPDModel, VerificationType, VersionStateType } from 'services/EpdClient';
import styled from 'styled-components';
import { FieldContainer, ReadMoreInlineButton } from 'styles/v2/Styles.styled';
import { formatAsUTCDate, formatDate } from 'util/utils';

type TProps = {
  epd: EPDModel;
  groupStatus?: EPDCopyGroupStatus;
  isVerifier?: boolean;
  onRemoveFile(fileId: string): Promise<void>;
  isReadOnly: boolean;
};

const EpdVerificationDescription = ({ epd, groupStatus, isVerifier, onRemoveFile, isReadOnly }: TProps) => {
  const { t } = useTranslation();
  const [versionState, setVersionState] = useState<string>(epd?.versionState ?? VersionStateType.Draft);
  const [isProcessCertificate, setIsProcessCertificate] = useState<boolean>(
    epd.verificationType == VerificationType.EPDProcessCertification
  );
  const [showVerificationDeclineReasonFullCommentDialog, setShowVerificationDeclineReasonFullCommentDialog] =
    useState(false);

  useEffect(() => {
    if (epd === undefined) {
      return;
    }

    setVersionState(epd.versionState ?? VersionStateType.Draft);
    setIsProcessCertificate(epd.verificationType == VerificationType.EPDProcessCertification);
  }, [epd]);

  const hasUploadedVerificationReports = () => {
    return (epd.verificationReports?.length ?? 0) > 0;
  };

  const hasUploadedProcessCertificates = () => {
    return (epd.processCertificates?.length ?? 0) > 0;
  };

  const getAllFiles = () => {
    if (!hasUploadedVerificationReports() && !hasUploadedProcessCertificates()) {
      return [];
    } else if (hasUploadedVerificationReports() && !hasUploadedProcessCertificates()) {
      return epd.verificationReports;
    } else if (!hasUploadedVerificationReports() && hasUploadedProcessCertificates()) {
      return epd.processCertificates;
    }
    return epd.verificationReports?.concat(epd.processCertificates || []);
  };

  const showDate = (date: any) => {
    if (date) {
      return formatDate(formatAsUTCDate(date));
    }
    return undefined;
  };

  const verificationDeclineReasonPostProcessed =
    epd.verificationDeclineReason &&
    (epd.verificationDeclineReason.length > STATUS_COMMENT_MAX_LENGTH
      ? epd.verificationDeclineReason.slice(0, STATUS_COMMENT_MAX_LENGTH) + '…'
      : epd.verificationDeclineReason);

  return (
    <>
      {!isProcessCertificate && (
        <>
          {(((groupStatus == EPDCopyGroupStatus.DeclinedPublication ||
            groupStatus == EPDCopyGroupStatus.DeclinedCopyByLicenseeAdmin) &&
            versionState == VersionStateType.Draft) ||
            versionState == VersionStateType.Approved ||
            versionState == VersionStateType.SentForPublicationLicenseeReview ||
            versionState == VersionStateType.SentForPublicationEditorial ||
            versionState == VersionStateType.SentForPublication ||
            versionState == VersionStateType.DeclinedPublication) && (
            <>
              {epd.verificationReports?.map((report: any) => (
                <FileRow key={report.id} file={report} disabled onRemoveFile={() => onRemoveFile(report.id)} />
              ))}
            </>
          )}
          {versionState == VersionStateType.DeclinedVerification && (
            <>
              <DeclineReport>
                {verificationDeclineReasonPostProcessed}{' '}
                {verificationDeclineReasonPostProcessed &&
                  verificationDeclineReasonPostProcessed.length > STATUS_COMMENT_MAX_LENGTH && (
                    <ReadMoreInlineButton onClick={() => setShowVerificationDeclineReasonFullCommentDialog(true)}>
                      Read more
                    </ReadMoreInlineButton>
                  )}
              </DeclineReport>
              <StatusCommentFullViewDialog
                headerTitle={t('epdWizard.epdState.action.commentByVerifierTitle')}
                comment={epd.verificationDeclineReason!}
                isOpened={showVerificationDeclineReasonFullCommentDialog}
                onClose={() => setShowVerificationDeclineReasonFullCommentDialog(false)}
              />
            </>
          )}
          {versionState == VersionStateType.UnderReview && hasUploadedVerificationReports() && (
            <>
              {epd.verificationReports?.map((report: any) => (
                <FileRow
                  key={report.id}
                  file={report}
                  disabled={epd.epdVerificationActionStates?.uploadVerificationReport == ActionState.Enabled}
                  onRemoveFile={() => onRemoveFile(report.id)}
                />
              ))}
            </>
          )}
          {versionState == VersionStateType.UnderReview && !hasUploadedVerificationReports() && (
            <>
              {isVerifier ? (
                <PendingReport>Please upload the verification report.</PendingReport>
              ) : (
                <PendingReport>No documents.</PendingReport>
              )}
            </>
          )}
        </>
      )}
      {!isProcessCertificate && epd.verificationType === VerificationType.EPDVerification && epd.approvalTimestamp && (
        <FieldContainerTimestamp>
          <WizardTextInputComponent
            label="Approval timestamp"
            name="approvalTimestamp"
            tooltip="This timestamp is automatically generated for the current EPD Copy when the verifier clicks “Approve“. The approval timestamp neither replaces nor corresponds to the approval date in the verification report."
            disabled
            required
            value={showDate(epd?.approvalTimestamp)}
          />
        </FieldContainerTimestamp>
      )}

      {isProcessCertificate && (
        <>
          {(hasUploadedVerificationReports() || hasUploadedProcessCertificates()) && (
            <>
              {getAllFiles()?.map((report: any) => (
                <FileRow key={report.id} file={report} disabled={isReadOnly} onRemoveFile={() => onRemoveFile(report.id)} />
              ))}
            </>
          )}
          {!hasUploadedVerificationReports() && !hasUploadedProcessCertificates() && (
            <PendingReport>Please upload a verification report and a process certificate if it is needed.</PendingReport>
          )}
        </>
      )}
    </>
  );
};

const FieldContainerTimestamp = styled(FieldContainer)`
  margin-top: 30px;
  margin-bottom: 10px;
`;

const DeclineReport = styled(HelpBox)`
  word-break: break-word;
  background-color: #f5d6d4;
  color: #e20238;
  flex-direction: row;
  padding: 14px 16px;
  align-items: center;
  margin: 10px 10px 10px 0px;
`;

const PendingReport = styled(HelpBox)`
  background-color: #f3f4fb;
  color: #151515;
  flex-direction: row;
  padding: 14px 16px;
  align-items: center;
  margin: 10px 10px 10px 0px;
`;

export default EpdVerificationDescription;
